"use client"

import React, {useEffect, useRef, useState} from "react";
import {TransformComponent, TransformWrapper} from "react-zoom-pan-pinch";
import PictureWithBlur from "@/components/utilities/picture-with-blur";
import FaIcon from "@/components/utilities/fa-icon";
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import {alpha, ButtonBase, Fade, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import {useAppSelector} from "@/redux/hooks";
import {RootState} from "@/redux/store";
import LayoutDialog from "@/components/layout/layout-dialog";
import NextImage from "next/image";
import AddRemoveOfferModal from "./add-remove-offer-modal";
import {useAddProductOfferMutation, useDeleteProductOfferMutation} from "@/redux/apis/product-api";
import Paper from "@mui/material/Paper";

export default function PPhotoGallery({product, mutateProduct}: { product: any; mutateProduct: () => void }) {
    const {isNewest, isOffer} = product || {};
    const pictures = product?.pictures || [];
    const theme = useTheme();
    const [validPictures, setValidPictures] = useState<string[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [open, setOpen] = useState(false);
    const [discount, setDiscount] = useState("");

    const isAdmin = useAppSelector((state: RootState) => state.user.currentUser?.roles?.includes("Admin"));
    const [addOffer, {isLoading: adding}] = useAddProductOfferMutation();
    const [deleteOffer, {isLoading: deleting}] = useDeleteProductOfferMutation();

    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    let imageSize = isMobile ? 260 : 320;

    const handleOpen = () => {
        setDiscount("");
        setOpen(true);
    };

    const handleClose = () => {
        setDiscount("");
        setOpen(false);
    };

    function isTouchDevice(): boolean {
        if (typeof window === "undefined" || typeof navigator === "undefined") {
            return false;
        }

        return "ontouchstart" in window || navigator.maxTouchPoints > 0;
    }

    useEffect(() => {
        if (!pictures?.length) {
            setValidPictures([]);
            return;
        }

        let mounted = true;

        Promise.all(
            pictures.map(
                (src) =>
                    new Promise<string | null>((resolve) => {
                        if (!src) return resolve(null);

                        const img = new Image();
                        img.src = src;

                        img.onload = () => resolve(src);
                        img.onerror = () => resolve(null);
                    })
            )
        ).then((res) => {
            if (mounted) {
                setValidPictures(res.filter(Boolean) as string[]);
            }
        });

        return () => {
            mounted = false;
        };
    }, [pictures]);

    const handleSubmit = async () => {
        try {
            if (isOffer) {
                await deleteOffer(product.id).unwrap();
            } else {
                await addOffer({
                    id: product.id,
                    discount: Number(discount),
                }).unwrap();
            }

            mutateProduct();
            handleClose();
        } catch (error) {
            console.error(error);
        }
    };

    const navButtonSx = {
        minWidth: "unset",
        width: {xs: 40, md: 44},
        height: {xs: 40, md: 44},
        position: "absolute",
        top: "50%",
        zIndex: 3,
        borderRadius: 2,
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: alpha(theme.palette.primary.main, 0.35),
        color: theme.palette.primary.main,
        background: alpha(theme.palette.background.paper, 0.92),
        backdropFilter: "blur(8px)",
        transition: "all 0.22s ease",
        "&.Mui-disabled": {
            opacity: 0.35,
            color: theme.palette.grey[400],
            borderColor: theme.palette.grey[200],
            background: alpha(theme.palette.background.paper, 0.75),
        },
        "&:hover": {
            borderColor: theme.palette.primary.main,
            background: theme.palette.primary.main,
            color: theme.palette.common.white,
            transform: "translateY(-50%) scale(1.06)",
        },
        "&:active": {
            transform: "translateY(-50%) scale(0.94)",
        },
    };

    return (
        <>

            <Paper
                ref = {containerRef}
                elevation = {0}
                sx = {{
                    width: "100%",
                    p: 3,
                    position: "relative",
                }}
            >
                <Box width = "100%" >
                    <TransformWrapper initialScale = {1} >
                        {({resetTransform, state}) => (
                            <React.Fragment >
                                <Button
                                    onClick = {() => {
                                        resetTransform(0);
                                        setActiveIndex((activeIndex + pictures?.length - 1) % pictures.length);
                                    }}
                                    disabled = {pictures.length < 1}
                                    sx = {{
                                        ...navButtonSx,
                                        left: {xs: 12, md: 22},
                                        transform: "translateY(-50%)",
                                    }}
                                >
                                    <FaIcon fontSize = {24} faIcon = "fa-arrow-right" />
                                </Button >

                                <Button
                                    onClick = {() => {
                                        resetTransform(0);
                                        setActiveIndex((activeIndex + 1) % pictures?.length);
                                    }}
                                    disabled = {pictures.length < 1}
                                    sx = {{
                                        ...navButtonSx,
                                        right: {xs: 12, md: 22},
                                        transform: "translateY(-50%)",
                                    }}
                                >
                                    <FaIcon fontSize = {24} faIcon = "fa-arrow-left" />
                                </Button >

                                <Stack
                                    direction = "row"
                                    justifyContent = "end"
                                    alignItems = "center"
                                    divider = {<Divider orientation = "vertical" flexItem />}
                                    sx = {{
                                        position: "absolute",
                                        top: 14,
                                        right: 14,
                                        zIndex: 4,
                                        gap: 1,
                                        p: 0.5,
                                        borderRadius: 2,
                                        bgcolor: alpha(theme.palette.background.paper, 0.82),
                                        border: `1px solid ${theme.palette.grey[200]}`,
                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    {!isAdmin && isOffer && (
                                        <Tooltip title = "پیشنهاد ویژه" >
                                            <IconButton
                                                sx = {{
                                                    width: 38,
                                                    height: 38,
                                                    color: theme.palette.secondary.main,
                                                }}
                                            >
                                                <FaIcon
                                                    justifyContent = "start"
                                                    alignItems = "start"
                                                    faIcon = "fa-trophy-alt"
                                                    fontSize = {{md: 24, xs: 22}}
                                                    variant = "light"
                                                />
                                            </IconButton >
                                        </Tooltip >
                                    )}

                                    {isAdmin && (
                                        <Tooltip title = "پیشنهاد ویژه" >
                                            <IconButton
                                                sx = {{
                                                    width: 38,
                                                    height: 38,
                                                    color: isOffer ? theme.palette.secondary.main : theme.palette.grey[400],
                                                    zIndex: 20,
                                                    bgcolor: isOffer ? alpha(theme.palette.secondary.main, 0.08) : "transparent",
                                                    "&:hover": {
                                                        bgcolor: isOffer ? alpha(theme.palette.secondary.main, 0.12) : "transparent",

                                                    },
                                                }}
                                                onClick = {handleOpen}
                                            >
                                                <FaIcon
                                                    justifyContent = "start"
                                                    alignItems = "start"
                                                    faIcon = "fa-trophy-alt"
                                                    fontSize = {{md: 24, xs: 22}}
                                                    variant = "light"
                                                />
                                            </IconButton >
                                        </Tooltip >
                                    )}

                                    {isNewest && (
                                        <Tooltip title = "کالای تازه رسیده" >
                                            <IconButton
                                                sx = {{
                                                    width: 38,
                                                    height: 38,
                                                    color: theme.palette.secondary.main,
                                                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                                    "&:hover": {
                                                        bgcolor: alpha(theme.palette.secondary.main, 0.12),
                                                    },
                                                }}
                                            >
                                                <FaIcon
                                                    justifyContent = "start"
                                                    alignItems = "start"
                                                    faIcon = "fa-dolly"
                                                    fontSize = {{md: 24, xs: 22}}
                                                    variant = "light"
                                                />
                                            </IconButton >
                                        </Tooltip >
                                    )}
                                </Stack >

                                <Box
                                    position = "relative"
                                    display = "flex"
                                    justifyContent = "center"
                                    alignItems = "center"
                                    minHeight = {{xs: 300, md: 352}}
                                >
                                    <TransformComponent
                                        wrapperStyle={{
                                            width: "100%",
                                            height: "100%",
                                        }}
                                        contentStyle={{
                                            width: "100%",
                                            height: "100%",
                                        }}
                                    >
                                        <Box
                                            position="relative"
                                            overflow="hidden"
                                            width="100%"
                                            height={{ md: 320, xs: 260 }}
                                        >
                                            {pictures.length > 0 ? (
                                                pictures.map((picture, index) => {
                                                    const offset = (index - activeIndex) * 100;

                                                    return (
                                                        <Box
                                                            key = {index}
                                                            position = "absolute"
                                                            display = "flex"
                                                            justifyContent = "center"
                                                            alignItems = "center"
                                                            width = "100%"
                                                            height = "100%"
                                                            top = {-20}
                                                            left = {0}
                                                            sx = {{
                                                                transform: `translateX(${offset}%)`,
                                                                transition: "transform 0.4s ease",
                                                            }}
                                                        >
                                                            <PictureWithBlur
                                                                src = {picture}
                                                                // layout="fixed"
                                                                width = {imageSize}
                                                                height = {imageSize}
                                                                alt = {product.namePersian}
                                                                quality = {100}

                                                            />
                                                        </Box >
                                                    );
                                                })
                                            ) : (
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    width="100%"
                                                    minHeight={{
                                                        xs: 100,
                                                        md: 290,
                                                    }}
                                                >
                                                    <NextImage
                                                        src="/images/icons/no-photo.svg"
                                                        width={imageSize}
                                                        height={imageSize - 100}
                                                        alt={
                                                            product?.namePersian ||
                                                            "عکسی برای کالا موجود نیست"
                                                        }
                                                        style={{
                                                            display: "block",
                                                            objectFit: "contain",
                                                            opacity: 0.8,
                                                        }}
                                                    />

                                                    <Typography
                                                        mt={1}
                                                        fontSize={14}
                                                        fontWeight={300}
                                                        color={theme.palette.grey[600]}
                                                        textAlign="center"
                                                    >
                                                        عکسی برای این کالا یافت نشد
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box >
                                    </TransformComponent >

                                    <Fade in = {state.scale === 1 && pictures.length > 0} >
                                        <Box
                                            position = "absolute"
                                            display = "flex"
                                            alignItems = "center"
                                            justifyContent = "center"
                                            width = "100%"
                                            bottom = {16}
                                        >
                                            <Box
                                                display = "flex"
                                                alignItems = "center"
                                                justifyContent = "center"
                                                px = {1.4}
                                                py = {0.9}
                                                bgcolor = {theme.palette.grey[600]}
                                                borderRadius = {2}
                                                sx = {{
                                                    backdropFilter: "blur(8px)",
                                                }}
                                            >
                                                <FaIcon color = "common.white" faIcon = "fa-search-plus" />
                                                <Typography fontSize = {12} ml = {1} color = "common.white" >
                                                    {!isTouchDevice() ? "برای بزرگنمایی اسکرول کنید" : "برای بزرگنمایی با دو انگشت بکشید"}
                                                </Typography >
                                            </Box >
                                        </Box >
                                    </Fade >
                                </Box >

                                {pictures.length > 0 &&
                                    <>
                                      <Divider />
                                      <Stack
                                          bgcolor = {theme.palette.grey[50]}
                                          px = {2}
                                          borderRadius={2}
                                          py = {1.5}
                                          spacing = {1.2}
                                          direction = "row"
                                          justifyContent = "center"
                                          alignItems = "center"
                                      >
                                          {pictures.map((picture, index) => (
                                              <ButtonBase
                                                  key = {index}
                                                  onClick = {() => {
                                                      setActiveIndex(index);
                                                      resetTransform(0);
                                                  }}
                                                  sx = {{
                                                      width: {xs: 66, md: 76},
                                                      height: {xs: 66, md: 76},
                                                      borderRadius: 2,
                                                      overflow: "hidden",
                                                      p: 0.35,
                                                      bgcolor: activeIndex === index ? alpha(theme.palette.primary.main, 0.10) : theme.palette.background.paper,
                                                      border: `1px solid ${activeIndex === index ? theme.palette.primary.main : "transparent"}`,
                                                      transition: "all 0.2s ease",
                                                      "&:hover": {
                                                          borderColor: activeIndex === index ? theme.palette.primary.main : theme.palette.grey[300],
                                                          transform: "translateY(-2px)",
                                                      },
                                                  }}
                                              >
                                                  <PictureWithBlur
                                                      src = {picture}
                                                      width = {70}
                                                      height = {70}
                                                      alt = {product.namePersian}
                                                  />
                                              </ButtonBase >
                                          ))}
                                      </Stack >

                                    </>
                                    }

                            </React.Fragment >
                        )}
                    </TransformWrapper >
                </Box >
            </Paper >

            <LayoutDialog
                open = {open}
                handleClose = {handleClose}
                title = {isOffer ? "حذف پیشنهاد ویژه" : "افزودن پیشنهاد ویژه"}
                maxWidth = "xs"
                fullWidth
                dialogActions = {
                    <Stack width = "100%" spacing = {2} direction = "row" >
                        <Button
                            onClick = {handleClose}
                            fullWidth
                            size = "large"
                            variant = "outlined"
                        >
                            لغو
                        </Button >

                        <Button
                            fullWidth
                            variant = "contained"
                            color = {isOffer ? "error" : "primary"}
                            onClick = {handleSubmit}
                            disabled = {adding || deleting}
                        >
                            {isOffer ? "حذف پیشنهاد" : "ذخیره"}
                        </Button >
                    </Stack >
                }
            >
                <AddRemoveOfferModal
                    isOffer = {isOffer}
                    discount = {discount}
                    setDiscount = {setDiscount}
                    onSubmit = {handleSubmit}
                />
            </LayoutDialog >
        </>
    );
}
